import { AppAlert } from '../../../theme';
import t from '../../../localization/en/translation.json';
import { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash'
import { useSelector } from 'react-redux';
import { APP_STATE } from '../../../store/reducers';
import { isAppActive } from '../../../utils/helper-utility';
import { IUpdateManageUsersResponse } from '../../../models/IUserAccess';
import './manageUserRoleError.scss';

interface IManageUserRoleErrorProps {
  canGridLoad: boolean;
  updateManageUsersResponse: IUpdateManageUsersResponse;
}

interface IErrorMessage {
  errorType: "error" | "warning" | "info" | "success";
  errorMessage: string;
}

const initErrorMessage: IErrorMessage = {
  errorType: "error",
  errorMessage: "",
};

const ManageUserRoleError: React.FC<IManageUserRoleErrorProps> = ({
  canGridLoad,
  updateManageUsersResponse,
}) => {
  const [showGridError, setShowGridError] = useState(false);
  const [errorMessages, setErrorMessages] = useState(initErrorMessage);
  const { record, usersRequested, userAccessRequested } = useSelector(
    (state: APP_STATE) => state.projects
  );
  const {
    roles: appPlatformRoles,
    projectStatuses,
    appPlatforms,
    rolesRequested,
  } = useSelector((state: APP_STATE) => state.meta);

  useEffect(() => {
    if (
      (rolesRequested === false && !usersRequested && !userAccessRequested) ||
      (usersRequested === false && !rolesRequested && !userAccessRequested) ||
      (userAccessRequested === false && !rolesRequested && !usersRequested)
    ) {
      handleErrorMessage();
    }
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, [rolesRequested, usersRequested, userAccessRequested]);

  const areAllPlatformRolesAvailable = () => {
    const ce4PlatformId = appPlatforms.find((p) => p.key === 'CE4')?.id;
    const isCe4PlatformAvailble = record?.appPlatformDetails?.find(
      (p) => p.appPlatformClassId === ce4PlatformId
    );
    let nonSilentPlatforms = cloneDeep(record?.appPlatformDetails);

    if (!!isCe4PlatformAvailble && nonSilentPlatforms) {
      const ce4Apps = record?.appDetails?.filter(
        (d) => d.appPlatformId === ce4PlatformId
      );

      nonSilentPlatforms =
        ce4Apps && !ce4Apps.length
          ? record?.appPlatformDetails?.filter(
              (p) => p.appPlatformClassId !== ce4PlatformId
            )
          : record?.appPlatformDetails;
    }

    const provisionedAppPltforms = (
      nonSilentPlatforms?.filter((item) => {
        return isAppActive(
          projectStatuses,
          record?.appPlatformDetails,
          item.appPlatformClassId || ''
        );
      }) || []
    ) // filtering applicationPlatformDetail has at least one application
      .filter((applicationPlatformDetail) =>
        record?.appDetails?.some(
          (applicationDetail) =>
            applicationDetail.appPlatformId ===
            applicationPlatformDetail.appPlatformClassId
        )
      );
    let downPlatforms: string = '';
    const platformRoles = appPlatformRoles.appPlatforms.filter(
      (item) => {
        if(item?.roles.length > 0) {
          return true;
        }else{
          downPlatforms += downPlatforms.length ? ', ' + platformName(item?.appPlatformKey) : platformName(item?.appPlatformKey); 
          return false;
        }
      }
    );
    let platformRolesReadyCount = 0;

    provisionedAppPltforms.forEach((item) => {
      const rolesList = platformRoles.find(
        (platformRole) => item.id === platformRole.appPlatformId
      );

      if (
        rolesList &&
        rolesList.roles?.filter((item) => item.name).length > 0
      ) {
        platformRolesReadyCount += 1;
      }
    });
    const isAllPlatformReady = platformRolesReadyCount === provisionedAppPltforms.length;
    return { isAllPlatformReady, downPlatforms };
  };

  const handleErrorMessage = () => {
    if (!canGridLoad) {
      setErrorMessages({
        errorType: 'error',
        errorMessage: t.manage_users_screen_load_error,
      });
      setShowGridError(true);
    }
    const { isAllPlatformReady, downPlatforms } = areAllPlatformRolesAvailable();
    if (canGridLoad && !isAllPlatformReady) {
      setErrorMessages({
        errorType: 'error',
        errorMessage: t.manage_users_grid_roles_fetch_failed.replace('{|platformName|}', downPlatforms),
      });
      setShowGridError(true);
    }
    const { failedPlatforms, updateErrors } = getPlatformRes();
    if (updateErrors) {
      setErrorMessages({
        errorType: 'error',
        errorMessage: t.manage_users_grid_update_fail.replace('{|platformName|}', failedPlatforms),
      });
      setShowGridError(true);
    }
  };

  const platformName = (platformKey) => {
    const appPlatform = appPlatforms.find((item) => item.key === platformKey);
    return appPlatform?.name;
  };

  const getPlatformRes = () => {
    let failedPlatforms: string = '';
    let updateErrors: boolean = false;
    updateManageUsersResponse?.appPlatforms?.forEach((item) => {
      if(!item.isAllProcessed) {
        updateErrors = true;
        failedPlatforms += failedPlatforms.length ? ', ' + platformName(item.appPlatform) : platformName(item.appPlatform); 
      }
    });
    return { failedPlatforms, updateErrors };
  }

  return (
    <>
      {showGridError && (
        <div className="grid-error">
          <AppAlert
            variant="outlined"
            severity={errorMessages.errorType}
            onClose={() => setShowGridError(false)}
          >
            {errorMessages.errorMessage}
          </AppAlert>
        </div>
      )}
    </>
  );
};

export default ManageUserRoleError;
